<template>
  <div class="login-page">
    <!-- Top navbar -->
    <div class="login-nav">
      <div class="login-nav__logo">
        <!-- The wordmark half of this logo is black, so the stock asset all but
             vanishes on the dark navbar. taxpod_logo_dark.png is the same file
             with its dark neutrals inverted onto the text tier — the brand cyan
             and the knocked-out "YYC" glyphs are identical in both. -->
        <img :src="logoSrc" alt="taxPOD" class="login-nav__logo-img" />
      </div>

      <div class="login-nav__spacer"></div>

      <!-- Dark mode toggle — same control as the console navbar, so the
           preference can be set before signing in. A native `title` stands in
           for the console's v-tooltip: this page is inside a `zoom: 1.1`
           wrapper, and a detached Vuetify overlay is positioned from an
           unzoomed coordinate space. -->
      <button
        class="darkmode-toggle"
        type="button"
        :title="isDarkMode ? 'Light Mode' : 'Dark Mode'"
        :aria-label="isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'"
        :aria-pressed="String(isDarkMode)"
        @click="toggleDarkMode()"
      >
        <v-icon size="20" :color="isDarkMode ? 'var(--app-warning)' : 'var(--app-text-muted)'">
          {{ isDarkMode ? 'mdi-white-balance-sunny' : 'mdi-moon-waning-crescent' }}
        </v-icon>
      </button>
    </div>

    <!-- Main content -->
    <div class="login-main">
      <!-- Left: Blue panel with tagline -->
      <div class="login-left">
        <div class="login-left__bg">
          <!-- Geometric shapes -->
          <div class="geo geo--1"></div>
          <div class="geo geo--2"></div>
          <div class="geo geo--3"></div>
          <div class="geo geo--4"></div>
        </div>
        <div class="login-left__content">
          <h1 class="login-left__title">
            Simplify Your<br />
            HRDC Claims,<br />
            From Submission<br />
            to Approval
          </h1>
        </div>
      </div>

      <!-- Mascot (positioned over the boundary) -->
      <div class="login-mascot-wrap">
        <img
          class="login-mascot"
          src="/images/tax-buddy.png"
          alt="YYC Tax Buddy"
        />
      </div>

      <!-- Right: Form area -->
      <div class="login-right">
        <!-- Decorative wave -->
        <div class="login-right__wave">
          <svg viewBox="0 0 600 800" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path class="login-wave__path" d="M600 800V0C600 0 580 200 500 350C420 500 300 550 250 650C200 750 300 800 600 800Z"/>
            <path class="login-wave__path login-wave__path--soft" d="M600 800V100C600 100 560 300 480 420C400 540 320 580 280 680C240 780 350 800 600 800Z"/>
          </svg>
        </div>

        <!-- Login card -->
        <div class="login-card">
          <h2 class="login-card__title">Welcome Back</h2>
          <p class="login-card__subtitle">Please log in to continue</p>

          <transition name="fade">
            <div v-if="api.isError" class="login-alert login-alert--error">
              <v-icon size="18" color="var(--app-danger-fg)" class="login-alert__icon">mdi-alert-circle-outline</v-icon>
              <span class="login-alert__text">{{ friendlyError }}</span>
            </div>
          </transition>

          <transition name="fade">
            <div v-if="message" class="login-alert login-alert--success">
              <v-icon size="18" color="var(--app-success-fg)" class="login-alert__icon">mdi-check-circle-outline</v-icon>
              <span class="login-alert__text">{{ message }}</span>
            </div>
          </transition>

          <!-- Email -->
          <div class="field">
            <label class="field__label">Email Address</label>
            <input
              type="email"
              class="field__input"
              placeholder="name@email.com"
              v-model="form.email"
              @keyup.enter="fetch"
            />
          </div>

          <!-- Password -->
          <div class="field">
            <label class="field__label">Password</label>
            <div class="field__input-wrap">
              <input
                :type="showPassword ? 'text' : 'password'"
                class="field__input"
                placeholder="Password"
                v-model="form.password"
                @keyup.enter="fetch"
              />
              <button class="field__toggle" @click="showPassword = !showPassword" type="button">
                <v-icon size="18" color="var(--app-text-faint)">{{ showPassword ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}</v-icon>
              </button>
            </div>
          </div>

          <!-- Remember + Forgot -->
          <div class="login-card__options">
            <label class="remember-check">
              <input type="checkbox" v-model="rememberMe" />
              <span class="remember-check__text">Remember Me</span>
            </label>
            <router-link :to="{ name: 'PageResetPassword' }" class="forgot-link">
              Forgot Password?
            </router-link>
          </div>

          <!-- Login Button -->
          <button
            class="login-btn"
            :disabled="api.isLoading"
            @click="fetch()"
          >
            <span v-if="!api.isLoading">Login</span>
            <span v-else class="login-btn__loading">
              <v-progress-circular indeterminate size="18" width="2" color="var(--app-login-btn-fg)"></v-progress-circular>
            </span>
          </button>

          <!-- First time login -->
          <div class="login-card__bottom">
            <router-link :to="{ name: 'PageGuestLoginFirstTime' }" class="firsttime-link">
              First Time Login?
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <AppFooter />
  </div>
</template>

<script>
import Api from "@/objects/api";
import HrdcData from "@/services/hrdc/objects/globalData";
import { themeState, toggleDark } from "@/plugins/theme";

export default {
  props: ["message"],
  computed: {
    // Reads the app-wide theme singleton (src/plugins/theme.js), which is the
    // same state the console navbar toggles — so a preference set here is
    // already in effect on the dashboard after login.
    isDarkMode() {
      return themeState.dark;
    },
    logoSrc() {
      return this.isDarkMode
        ? "/images/taxpod_logo_dark.png"
        : "/images/taxpod_logo.png";
    },
    friendlyError() {
      const err = this.api.error;
      if (!err) return "";

      const errStr = String(err).toLowerCase();

      // Database connection errors
      if (errStr.includes("can't reach database") || errStr.includes("database server")) {
        return "Unable to connect to the server. Please try again later or contact support.";
      }

      // Prisma / internal server errors
      if (errStr.includes("prisma") || errStr.includes("invocation in") || errStr.includes("throw new error")) {
        return "Something went wrong on our end. Please try again later or contact support.";
      }

      // Network errors
      if (errStr.includes("network error") || errStr.includes("econnrefused") || errStr.includes("timeout")) {
        return "Unable to reach the server. Please check your internet connection and try again.";
      }

      // Common auth errors — pass through as-is (they're already user-friendly)
      if (errStr.includes("password") || errStr.includes("email") || errStr.includes("invalid") || errStr.includes("incorrect") || errStr.includes("not found") || errStr.includes("unauthorized")) {
        return err;
      }

      // Stack traces or file paths — generic message
      if (errStr.includes("/") && errStr.includes(".js")) {
        return "An unexpected error occurred. Please try again later or contact support.";
      }

      // Default — return as-is if it's short enough, otherwise generic
      if (String(err).length > 150) {
        return "An unexpected error occurred. Please try again later or contact support.";
      }

      return err;
    },
  },
  data: () => ({
    form: {
      email: null,
      password: null,
    },
    api: new Api(),
    hrdcData: new HrdcData(),
    showPassword: false,
    rememberMe: false,
  }),
  created() {
    this.api.setMethod(`POST`);
    this.api.setUrl(`${this.$service.sso}/v1/en/auth/login`);
    this.api.setCallbackCompleted((resp) => {
      this.$store.commit("updateAuth", resp);
      this.$router.push({ name: "PageConsoleDashboard" });
    });
  },
  methods: {
    fetch() {
      this.api.setParams(this.form);
      this.api.fetch();
    },
    toggleDarkMode() {
      toggleDark();
    },
  },
};
</script>

<style scoped>
/* ===== DARK MODE =====
   This page now owns its whole dark design by hand, the same call
   PageConsoleDashboard.vue made. The generated companion set that used to live
   here only ever reached the navbar and the card, because the login page is not
   the page → card → field ramp the generator assumes: it is two full-bleed
   halves (a saturated brand panel and a washed background) with a card floating
   on one of them. Everything it did not name — .login-page, .login-left,
   .login-right, the wave — kept its light value, which is why dark mode
   rendered as a dark card on a bright page.

   The panel, the geo shapes, the wave and the CTA gradient are themed at their
   BASE rule via --app-login-*, so there is nothing to override for them here.
   Colours below are token references only (--app-*, src/plugins/theme-dark.css)
   — no literal hexes. */

.dark-mode .login-page {
  background: var(--app-page);
}

.dark-mode .login-nav {
  background: var(--app-navbar);
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .darkmode-toggle:hover {
  background-color: var(--app-hover);
}

.dark-mode .login-right {
  background: var(--app-page);
}

.dark-mode .login-mascot {
  filter: drop-shadow(0 8px 24px var(--app-shadow-deep));
}

.dark-mode .login-card {
  background: var(--app-card);
  border-color: var(--app-border);
  box-shadow: 0 2px 12px var(--app-shadow-mid),
    inset 0 1px 0 var(--app-inner-highlight);
}

.dark-mode .login-card__title {
  color: var(--app-text);
}

.dark-mode .login-card__subtitle {
  color: var(--app-text-faint);
}

.dark-mode .login-alert--error {
  background: var(--app-danger-bg);
  border-color: var(--app-danger-border);
  color: var(--app-danger-fg);
}

.dark-mode .login-alert--success {
  background: var(--app-success-bg);
  border-color: var(--app-success-border);
  color: var(--app-success-fg);
}

.dark-mode .field__label {
  color: var(--app-text);
}

/* A field sits one step ABOVE the card it is on — --app-card here would make
   each input a hole in the card rather than a target to click into. */
.dark-mode .field__input {
  background: var(--app-field);
  border-color: var(--app-field-border);
  color: var(--app-text);
}

.dark-mode .field__input::placeholder {
  color: var(--app-field-placeholder);
}

.dark-mode .field__input:focus {
  border-color: var(--app-accent);
  box-shadow: 0 0 0 3px var(--app-ring);
}

.dark-mode .remember-check input[type="checkbox"] {
  accent-color: var(--app-accent);
}

.dark-mode .remember-check__text {
  color: var(--app-text-muted);
}

.dark-mode .forgot-link {
  color: var(--app-text);
}

.dark-mode .forgot-link:hover {
  color: var(--app-accent);
}

.dark-mode .firsttime-link {
  color: var(--app-accent);
}

/* ===== end dark mode ===== */

/* ===== PAGE ===== */
.login-page {
  /* `zoom: 1.1` below scales this box AFTER layout, so a plain 100vh paints at
     110vh and the bottom tenth — the footer — falls off the screen behind
     `overflow: hidden`. Dividing by the zoom makes the painted height 100vh. */
  height: calc(100vh / 1.1);
  display: flex;
  flex-direction: column;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  background: #f0f4f8;
  zoom: 1.1;
  overflow: hidden;
}

/* ===== TOP NAVBAR ===== */
.login-nav {
  height: 56px;
  background: #fff;
  display: flex;
  align-items: center;
  padding: 0 32px;
  border-bottom: 1px solid #e8eaed;
  flex-shrink: 0;
  z-index: 10;
}

.login-nav__logo {
  display: flex;
  align-items: center;
  gap: 8px;
}

.login-nav__spacer {
  flex: 1;
}

/* Mirrors the console navbar's .darkmode-toggle so the control is the same
   object in both places. */
.darkmode-toggle {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease;
}

.darkmode-toggle:hover {
  background-color: rgba(0, 0, 0, 0.06);
}

.login-nav__logo-img {
  height: 32px;
  object-fit: contain;
}

/* ===== MAIN ===== */
.login-main {
  flex: 1;
  display: flex;
  min-height: 0;
  position: relative;
}

/* ===== LEFT BLUE PANEL ===== */
.login-left {
  flex: 0 0 42%;
  background: var(--app-login-panel);
  position: relative;
  overflow: visible;
  display: flex;
  align-items: center;
  z-index: 2;
}

.login-left__bg {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

/* Geometric diamond/triangle shapes */
.geo {
  position: absolute;
  background: var(--app-login-geo);
}

.geo--1 {
  width: 300px;
  height: 300px;
  top: -50px;
  left: -80px;
  transform: rotate(45deg);
  border: 1px solid var(--app-login-geo-border);
}

.geo--2 {
  width: 200px;
  height: 200px;
  top: 30%;
  left: 25%;
  transform: rotate(30deg);
  border: 1px solid var(--app-login-geo-border);
}

.geo--3 {
  width: 250px;
  height: 250px;
  bottom: -60px;
  left: 10%;
  transform: rotate(60deg);
  border: 1px solid var(--app-login-geo-border);
}

.geo--4 {
  width: 150px;
  height: 150px;
  top: 15%;
  right: -30px;
  transform: rotate(15deg);
  border: 1px solid var(--app-login-geo-border);
}

.login-left__content {
  position: relative;
  z-index: 1;
  padding: 60px;
}

.login-mascot-wrap {
  position: absolute;
  left: 42%;
  /* Offset from .login-main's bottom, which is now the footer's top edge rather
     than the viewport's — raised by roughly the footer height so the mascot
     keeps the same visible framing it had before the footer existed. */
  bottom: -35px;
  z-index: 3;
  pointer-events: none;
  transform: translateX(-50%);
}

/* Raise the mascot on shorter viewports (e.g. 1280x720) so its bottom
   isn't clipped off the screen. */
@media (max-height: 820px) {
  .login-mascot-wrap {
    bottom: 20px;
  }
}


.login-mascot {
  /* Scale mascot with viewport so it never overlaps the login card.
     Formula keeps a ~16px gap from the card's left edge across viewports,
     accounting for the .login-page zoom: 1.1 wrapper. Max 564px CSS
     renders at 620px on screen — the original design size. */
  width: clamp(180px, calc(52.7vw - 370px), 564px);
  height: auto;
  display: block;
  user-select: none;
  filter: drop-shadow(0 8px 24px rgba(0, 0, 0, 0.15));
}

.login-left__title {
  font-size: 1.8rem;
  font-weight: 800;
  color: var(--app-login-panel-fg);
  line-height: 1.35;
  margin: 0;
}

/* ===== RIGHT PANEL ===== */
.login-right {
  flex: 1;
  background: #f0f4f8;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: 40px;
  padding-left: 80px;
}

.login-right__wave {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 60%;
  height: 100%;
  pointer-events: none;
}

.login-right__wave svg {
  width: 100%;
  height: 100%;
}

/* Painted from CSS rather than a `fill` attribute so the wave follows the
   theme like everything else. */
.login-wave__path {
  fill: var(--app-login-wave);
}

.login-wave__path--soft {
  fill: var(--app-login-wave-soft);
}

/* ===== LOGIN CARD ===== */
.login-card {
  width: 100%;
  max-width: 380px;
  background: #fff;
  border-radius: 16px;
  padding: 36px 32px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid #e4e8ec;
  position: relative;
  z-index: 1;
}

.login-card__title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px;
}

.login-card__subtitle {
  font-size: 0.85rem;
  color: #94a3b8;
  margin: 0 0 28px;
  font-weight: 400;
}

/* ===== ALERTS ===== */
.login-alert {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 0.82rem;
  font-weight: 500;
}

.login-alert--error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
}

.login-alert--success {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #16a34a;
}

.login-alert__icon {
  flex-shrink: 0;
}

.login-alert__text {
  line-height: 1.4;
}

/* ===== FORM FIELDS ===== */
.field {
  margin-bottom: 20px;
}

.field__label {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 6px;
}

.field__input-wrap {
  position: relative;
}

.field__input {
  width: 100%;
  padding: 11px 14px;
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.88rem;
  font-family: 'Lato', sans-serif;
  color: #1e293b;
  background: #fff;
  transition: all 0.2s ease;
  outline: none;
}

.field__input::placeholder {
  color: #b0b8c1;
}

.field__input:focus {
  border-color: #60b8e0;
  box-shadow: 0 0 0 3px rgba(96, 184, 224, 0.1);
}

.field__toggle {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
}

/* ===== OPTIONS ROW ===== */
.login-card__options {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.remember-check {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.remember-check input[type="checkbox"] {
  width: 15px;
  height: 15px;
  accent-color: #60b8e0;
  cursor: pointer;
}

.remember-check__text {
  font-size: 0.8rem;
  color: #475569;
  font-weight: 500;
}

.forgot-link {
  font-size: 0.8rem;
  color: #1e293b;
  font-weight: 700;
  text-decoration: none;
  transition: color 0.15s;
}

.forgot-link:hover {
  color: #3a9fd4;
}

/* ===== LOGIN BUTTON ===== */
.login-btn {
  width: 100%;
  padding: 13px;
  background: var(--app-login-btn);
  color: var(--app-login-btn-fg);
  border: none;
  border-radius: 8px;
  font-size: 0.92rem;
  font-weight: 700;
  font-family: 'Lato', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: 0.3px;
}

.login-btn:hover {
  background: var(--app-login-btn-hover);
  box-shadow: 0 4px 14px var(--app-login-btn-glow);
}

.login-btn:disabled {
  opacity: 0.7;
  cursor: wait;
}

.login-btn__loading {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ===== BOTTOM LINK ===== */
.login-card__bottom {
  text-align: center;
  margin-top: 20px;
}

.firsttime-link {
  font-size: 0.8rem;
  color: #3a9fd4;
  text-decoration: none;
  font-weight: 600;
}

.firsttime-link:hover {
  text-decoration: underline;
}

/* ===== MOBILE ===== */
@media (max-width: 768px) {
  .login-main {
    flex-direction: column;
  }

  .login-left {
    flex: none;
    padding: 40px 28px;
    min-height: 180px;
  }

  .login-left__content {
    padding: 0;
  }

  .login-mascot-wrap {
    display: none;
  }

  .login-left__title {
    font-size: 1.3rem;
  }

  .login-right {
    padding-left: 20px !important;
  }

  .login-right {
    padding: 28px 20px;
  }

  .login-card {
    max-width: 100%;
    padding: 28px 24px;
  }
}

/* ===== TABLET ===== */
@media (min-width: 769px) and (max-width: 1024px) {
  .login-left {
    flex: 0 0 36%;
  }

  .login-left__content {
    padding: 40px;
  }

  .login-left__title {
    font-size: 1.45rem;
  }

  /* Right panel is too narrow at this size to safely fit the mascot
     alongside the login card without overlap. */
  .login-mascot-wrap {
    display: none;
  }
}
</style>
